import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import Bugsnag from "@bugsnag/js";
import Draggable from "react-draggable";
import { useNavigate, useLocation } from "react-router-dom";

import useMobileView from "../../../hooks/useMobileView";
import { Case, Default, Switch } from "../ConditionalRendering";
import FlexBox from "../FlexBox";
import { Modal } from "../Modal";
import AiChatIntroScreen from "./AiChatIntroScreen";
import NewChatView, { AI_CHAT_PROMPTS_SESSION_KEY } from "./NewChatView";
import AiChatHistory from "./AiChatHistory";
import AIChat from "../../../assets/AiChat/ai-chat.svg";
import AIChatOpen from "../../../assets/AiChat/ai-chat-open.svg";
import AiChatHelpView from "./AiChatHelpView";
import AiChatHelpCentreView from "./AiChatHelpCentreView";
import SessionTranscriptionModal from "../../AIHome/SessionTranscriptionModal";
import SessionCustomisationModal from "../../AIHome/SessionCustomisationModal";
import axiosInstance from "../../../axiosInstance";
import URL from "../../../urls";
import {
  fetchTwaAIUIEvents,
  markTwaAIEventShown,
} from "../../../Store/Actions/twaAIActions";
import dayjs from "dayjs";
import { trackEvent } from "../../../helperFunctions";
import { NumberParam, useQueryParam } from "use-query-params";
import {
  setIsChatOpen,
  setChatView,
  setSelectedConversation,
  setConversationPagination,
  setMessageSent,
  clearChatSession,
  setLastChatConversationId,
} from "../../../Store/Actions/aiChatActions";

const AI_CHAT_OPEN_SESSION_KEY = "aiChatOpen";
const AI_CHAT_GREETING_SESSION_KEY = "aiChatGreeting";

const ChatContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: 999999 !important;
`;

const DraggableWrapper = styled.div`
  position: fixed;
  touch-action: none;
  cursor: grab;
  z-index: 999999 !important;

  transition: ${({ $isDragging, $isMounted }) =>
    $isMounted && !$isDragging
      ? "transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
      : "none"};

  &:active {
    cursor: grabbing;
  }
`;

const AiButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent !important;
  outline: none;
  overflow: hidden;
  transition: transform 0.2s ease;
  cursor: pointer;
  padding: 0;

  &:hover {
    transform: scale(1.1);
  }
`;

const ChatWindow = styled(FlexBox)`
  position: fixed;
  top: 50%;
  right: 4rem;
  transform: translateY(-50%);
  z-index: 9999999 !important;

  width: min(27rem, 90vw);
  height: min(37.5rem, 85vh);
  background: white;
  border-radius: 0.5rem 0 0 0.5rem;
  overflow: hidden;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  box-shadow: -8px 0px 32px rgba(0, 0, 0, 0.24);

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const FloatingTriggerWrapper = styled.div`
  position: fixed;
  right: 0rem;
  z-index: 10000000 !important;
`;

const AiIconImage = styled.img`
  width: 3rem;
  height: 3rem;
`;

const AiChat = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [clientId] = useQueryParam("client_id", NumberParam);

  const user = useSelector(state => state.auth.user);
  const twaAIEvents = useSelector(state => state.twaAI?.events || []);

  const isChatOpen = useSelector(state => state.aiChat.isChatOpen);
  const chatView = useSelector(state => state.aiChat.chatView);
  const selectedConversation = useSelector(
    state => state.aiChat.selectedConversation
  );
  const messages = useSelector(state => state.aiChat.messages);
  const lastChatConversationId = useSelector(
    state => state.aiChat.lastChatConversationId
  );
  const conversationPagination = useSelector(
    state => state.aiChat.conversationPagination
  );

  const shouldShowIntroModal = twaAIEvents.some(
    event => event.event_type === "intro_modal"
  );

  const [prefilledPrompt, setPrefilledPrompt] = useState("");
  const [isTranscriptionModalOpen, setIsTranscriptionModalOpen] =
    useState(false);
  const [isCustomisationModalOpen, setIsCustomisationModalOpen] =
    useState(false);

  const [isTranscriptionEnabled, setIsTranscriptionEnabled] = useState(false);
  const [openingConversationId, setOpeningConversationId] = useState(null);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [lastConversationFetchedId, setLastConversationFetchedId] =
    useState(null);
  const [thinking, setThinking] = useState(false);

  const openConversationAbortRef = useRef(null);
  const textareaRef = useRef(null);

  const isMobile = useMobileView();
  const draggableRef = useRef(null);

  const CORNER_SPACING = 32;
  const EXTRA_MOBILE_SPACING = 40;
  const BUTTON_SIZE = 48;

  const dragStartCoords = useRef({ x: 0, y: 0 });

  const getRightX = () => window.innerWidth - BUTTON_SIZE;
  const getTopY = () =>
    isMobile ? CORNER_SPACING + EXTRA_MOBILE_SPACING : CORNER_SPACING;
  const getBottomY = () => {
    const space = CORNER_SPACING + (isMobile ? EXTRA_MOBILE_SPACING : 0);
    return window.innerHeight - BUTTON_SIZE - space;
  };

  const getInitialPosition = () => {
    if (typeof window === "undefined") return { x: 0, y: 0 };
    return { x: getRightX(), y: window.innerHeight / 2 - BUTTON_SIZE / 2 };
  };

  const [position, setPosition] = useState(getInitialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [controlledPosition, setControlledPosition] = useState(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(false);
    const initial = getInitialPosition();
    setPosition(initial);
    setControlledPosition(initial);

    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [isMobile]);

  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => {
        const next = {
          x: getRightX(),
          y: Math.max(getTopY(), Math.min(prev.y, getBottomY())),
        };
        setControlledPosition(next);
        return next;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  const handleDrag = (e, data) => {
    setControlledPosition({ x: data.x, y: data.y });
  };

  const handleDragStart = (e, data) => {
    setIsDragging(true);
    dragStartCoords.current = { x: data.x, y: data.y };
    setControlledPosition({ x: data.x, y: data.y });
  };

  const handleDragStop = (e, data) => {
    setIsDragging(false);
    const currentY = data.y;

    const distanceTraveled = Math.hypot(
      data.x - dragStartCoords.current.x,
      currentY - dragStartCoords.current.y
    );

    if (distanceTraveled < 5) {
      setControlledPosition({ x: position.x, y: position.y });
      toggleChat();
      return;
    }

    const boundedY = Math.max(getTopY(), Math.min(currentY, getBottomY()));
    const finalPos = { x: getRightX(), y: boundedY };

    setPosition(finalPos);
    setControlledPosition(finalPos);
  };

  const fetchConsent = useCallback(async () => {
    try {
      if (!user?.id) return;

      const res = await axiosInstance.get(URL.aiPermission, {
        params: { user_ids: user.id },
      });

      setIsTranscriptionEnabled(res?.data?.data?.[user.id] ?? false);
    } catch (err) {
      Bugsnag.notify(err);
    }
  }, [user?.id]);

  useEffect(() => {
    dispatch(fetchTwaAIUIEvents());
  }, [dispatch]);

  useEffect(() => {
    fetchConsent();
  }, [fetchConsent]);

  useEffect(() => {
    sessionStorage.setItem(AI_CHAT_OPEN_SESSION_KEY, String(isChatOpen));
    if (!isChatOpen) {
      sessionStorage.removeItem(AI_CHAT_GREETING_SESSION_KEY);
      sessionStorage.removeItem(AI_CHAT_PROMPTS_SESSION_KEY);
      dispatch(clearChatSession());
    }
  }, [isChatOpen, dispatch]);

  useEffect(() => {
    if (!isChatOpen) return;
    fetchConsent();
  }, [isChatOpen, fetchConsent]);

  useEffect(() => {
    const handleUnload = () => {
      sessionStorage.removeItem(AI_CHAT_OPEN_SESSION_KEY);
      sessionStorage.removeItem("lastChatConversationId");
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  const toggleChat = () => {
    const nextChatOpen = !isChatOpen;

    setThinking(false);

    if (isMobile) {
      const params = new URLSearchParams(location.search);

      if (nextChatOpen) {
        params.set("showaichatmodal", "true");
        navigate(
          {
            pathname: location.pathname,
            search: `?${params.toString()}`,
          },
          { replace: false }
        );
        dispatch(setIsChatOpen(true));
      } else {
        if (params.get("showaichatmodal") === "true") {
          navigate(-1);
        } else {
          dispatch(setIsChatOpen(false));
        }
      }
    } else {
      dispatch(setIsChatOpen(nextChatOpen));
    }

    trackEvent({
      event: nextChatOpen ? "twa_ai_assistant_open" : "twa_ai_assistant_close",
      payload: {
        client_id: selectedClientId || clientId || null,
        source: clientId ? "client" : "dashboard",
      },
    });

    setSelectedClientId(clientId || null);
    dispatch(setSelectedConversation(null));
    dispatch(setChatView(shouldShowIntroModal ? "intro" : "new"));

    if (nextChatOpen) {
      if (lastChatConversationId) {
        handleOpenConversation({ id: lastChatConversationId }, true);
      }

      setTimeout(() => {
        textareaRef.current?.focus();
      }, 300);
    } else {
      const lastMessageId = messages?.at(-1)?.conversationId;
      if (lastChatConversationId || lastMessageId) {
        dispatch(
          setLastChatConversationId(lastMessageId || lastChatConversationId)
        );
      } else {
        dispatch(setLastChatConversationId(null));
      }
    }
  };

  useEffect(() => {
    if (!isMobile) return;

    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);

      if (!params.has("showaichatmodal") && isChatOpen) {
        setThinking(false);
        dispatch(setIsChatOpen(false));

        const lastMessageId = messages?.at(-1)?.conversationId;
        if (lastChatConversationId || lastMessageId) {
          dispatch(
            setLastChatConversationId(lastMessageId || lastChatConversationId)
          );
        } else {
          dispatch(setLastChatConversationId(null));
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isMobile, isChatOpen, dispatch, messages, lastChatConversationId]);

  const handleStartNewChat = () => {
    sessionStorage.removeItem(AI_CHAT_GREETING_SESSION_KEY);
    dispatch(setSelectedConversation(null));
    dispatch(clearChatSession());
    dispatch(setChatView("new"));
    textareaRef.current?.focus();
  };

  const handleOpenConversation = async (conversation, messageSent = false) => {
    if (!conversation?.id) return;

    if (openConversationAbortRef.current) {
      openConversationAbortRef.current.abort();
    }

    const controller = new AbortController();
    openConversationAbortRef.current = controller;

    setOpeningConversationId(conversation.id);
    sessionStorage.removeItem(AI_CHAT_GREETING_SESSION_KEY);
    dispatch(setSelectedConversation(null));
    dispatch(clearChatSession());
    try {
      const response = await axiosInstance.get(
        `${URL.aiChatConversations}/${conversation.id}`,
        {
          params: { page: 1, limit: 10 },
          signal: controller.signal,
        }
      );

      const conversationData = response?.data?.data;
      dispatch(
        setSelectedConversation({
          id: conversationData?.id || conversation.id,
          title: conversationData?.title || conversation.title,
          status: conversationData?.status || conversation.status,
          clientName:
            conversationData?.client?.name || conversation.clientName || "",
          clientCid: conversationData?.metadata?.client_id || "",
          messages: mapConversationChatsToMessages(
            conversationData?.chats,
            conversationData
          ),
        })
      );
      dispatch(setLastChatConversationId(null));
      if (lastChatConversationId) {
        setLastConversationFetchedId(lastChatConversationId);
      }

      const pagination = conversationData?.chats_pagination || {};

      dispatch(
        setConversationPagination({
          page: pagination.page || 1,
          limit: pagination.limit || 10,
          totalPages: pagination.total_pages || 1,
          totalRecords: pagination.total_records || 0,
          hasMore: (pagination.page || 1) < (pagination.total_pages || 1),
        })
      );

      dispatch(setMessageSent(messageSent));
      dispatch(setChatView("new"));
    } catch (error) {
      if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        return;
      }
      Bugsnag.notify(error);
    } finally {
      if (openConversationAbortRef.current === controller) {
        openConversationAbortRef.current = null;
      }
      setOpeningConversationId(null);
      textareaRef.current?.focus();
    }
  };

  const loadMoreConversationChats = async () => {
    if (
      !selectedConversation?.id ||
      isLoadingMoreChats ||
      !conversationPagination.hasMore
    ) {
      return [];
    }

    try {
      setIsLoadingMoreChats(true);
      const nextPage = conversationPagination.page + 1;

      const response = await axiosInstance.get(
        `${URL.aiChatConversations}/${selectedConversation.id}`,
        {
          params: { page: nextPage, limit: 10 },
        }
      );

      const conversationData = response?.data?.data;
      const olderMessages = mapConversationChatsToMessages(
        conversationData?.chats,
        conversationData
      );

      const pagination = conversationData?.chats_pagination || {};

      dispatch(
        setConversationPagination({
          page: pagination.page || nextPage,
          limit: pagination.limit || 10,
          totalPages: pagination.total_pages || 1,
          totalRecords: pagination.total_records || 0,
          hasMore:
            (pagination.page || nextPage) < (pagination.total_pages || 1),
        })
      );

      return olderMessages;
    } catch (error) {
      Bugsnag.notify(error);
      return [];
    } finally {
      setIsLoadingMoreChats(false);
    }
  };

  const openHelpCentre = () => {
    dispatch(setChatView("help-centre"));
  };

  const openTranscriptionSettings = () => {
    dispatch(setIsChatOpen(false));
    setIsTranscriptionModalOpen(true);
  };

  const closeTranscriptionSettings = () => {
    setIsTranscriptionModalOpen(false);
    fetchConsent();
  };

  const openCustomisationSettings = () => {
    dispatch(setIsChatOpen(false));
    setIsCustomisationModalOpen(true);
  };

  const handleIntroCompleted = () => {
    dispatch(markTwaAIEventShown("intro_modal"));
    dispatch(setMessageSent(false));
    dispatch(setChatView("new"));
    textareaRef.current?.focus();
  };

  const mapMessageSender = messageFrom => {
    if (messageFrom === "ai") return "ai";
    return "user";
  };

  const mapConversationChatsToMessages = (chats, conversationData) =>
    (Array.isArray(chats) ? chats : [])
      .map((chat, index) => ({
        id: chat?.id || `chat-message-${index}`,
        sender: mapMessageSender(chat?.message_from),
        markdownText: chat?.message?.content || "",
        feedback: chat?.feedback,
        isReported: Boolean(chat?.reported),
        timestamp: chat?.created_at,
        created_at: chat?.created_at,
        conversationThreadId: chat?.conversation_thread_id,
        externalConversationThreadId: chat?.external_thread_id,
        clientName: chat?.metadata?.client_name || null,
        clientCid: chat?.metadata?.client_id || null,
        conversationId: conversationData?.id || null,

        ...(chat?.is_error
          ? {
              errorInfo: {
                errorTitle: "We couldn’t generate a response.",
                errorDescription: `A response couldn’t be generated due to ${chat?.metadata?.llm_error?.message} reason.`,
                errorSuggestion:
                  "Try rephrasing your prompt. If the issue persists, reach out to support.",
              },
            }
          : {}),
      }))
      .sort(
        (firstItem, secondItem) =>
          dayjs(firstItem?.created_at).valueOf() -
          dayjs(secondItem?.created_at).valueOf()
      );

  const renderChatContent = () => (
    <Switch>
      <Case condition={chatView === "intro"}>
        <AiChatIntroScreen
          onClose={toggleChat}
          toggleIntroScreen={handleIntroCompleted}
        />
      </Case>

      <Case condition={chatView === "new"}>
        <NewChatView
          onHistoryClick={() => dispatch(setChatView("history"))}
          onClose={toggleChat}
          toggleHelp={() => dispatch(setChatView("help"))}
          prefilledPrompt={prefilledPrompt}
          onPromptPrefilled={() => setPrefilledPrompt("")}
          activeConversation={selectedConversation}
          onNewChatStarted={() => dispatch(setSelectedConversation(null))}
          isChatOpen={isChatOpen}
          onLoadMoreMessages={loadMoreConversationChats}
          hasMoreMessages={conversationPagination.hasMore}
          isLoadingMoreMessages={isLoadingMoreChats}
          openCustomisationSettings={openCustomisationSettings}
          setSelectedClientId={setSelectedClientId}
          selectedClientId={selectedClientId}
          textareaRef={textareaRef}
          lastConversationFetchedId={lastConversationFetchedId}
          setLastConversationFetchedId={setLastConversationFetchedId}
          thinking={thinking}
          setThinking={setThinking}
        />
      </Case>

      <Case condition={chatView === "history"}>
        <AiChatHistory
          onBack={() => dispatch(setChatView("new"))}
          onStart={handleStartNewChat}
          onOpenConversation={handleOpenConversation}
          openingConversationId={openingConversationId}
        />
      </Case>

      <Case condition={chatView === "help"}>
        <AiChatHelpView
          onBack={() => dispatch(setChatView("new"))}
          onOpenHelpCentre={openHelpCentre}
          onOpenTranscriptionSettings={openTranscriptionSettings}
          onOpenCustomisationSettings={openCustomisationSettings}
          isTranscriptionEnabled={isTranscriptionEnabled}
        />
      </Case>

      <Case condition={chatView === "help-centre"}>
        <AiChatHelpCentreView onBack={() => dispatch(setChatView("help"))} />
      </Case>
    </Switch>
  );

  if (typeof window === "undefined") return null;

  return createPortal(
    <ChatContainer>
      {!isMobile && (
        <ChatWindow $isOpen={isChatOpen}>{renderChatContent()}</ChatWindow>
      )}
      <Switch>
        <Case condition={isMobile}>
          {isChatOpen ? (
            <Modal M1 borderRadius="1rem" backgroundClickDisabled>
              {renderChatContent()}
            </Modal>
          ) : (
            <Draggable
              nodeRef={draggableRef}
              axis="y"
              position={controlledPosition}
              onDrag={handleDrag}
              onStart={handleDragStart}
              onStop={handleDragStop}
              bounds={{
                top: getTopY(),
                bottom: getBottomY(),
              }}
            >
              <DraggableWrapper
                ref={draggableRef}
                $isDragging={isDragging}
                $isMounted={isMounted}
              >
                <AiButton isChatOpen={isChatOpen}>
                  <AiIconImage src={AIChat} alt="AI Chat" />
                </AiButton>
              </DraggableWrapper>
            </Draggable>
          )}
        </Case>

        <Default>
          {isChatOpen ? (
            <FloatingTriggerWrapper style={{ top: `${position.y}px` }}>
              <AiButton onClick={toggleChat} isChatOpen={isChatOpen}>
                <AiIconImage src={AIChatOpen} alt="AI Chat Open" />
              </AiButton>
            </FloatingTriggerWrapper>
          ) : (
            <Draggable
              nodeRef={draggableRef}
              axis="y"
              position={controlledPosition}
              onDrag={handleDrag}
              onStart={handleDragStart}
              onStop={handleDragStop}
              handle=".drag-handle"
              bounds={{
                top: getTopY(),
                bottom: getBottomY(),
              }}
            >
              <DraggableWrapper
                ref={draggableRef}
                $isDragging={isDragging}
                $isMounted={isMounted}
                className="drag-handle"
              >
                <AiButton isChatOpen={isChatOpen}>
                  <AiIconImage src={AIChat} alt="AI Chat" />
                </AiButton>
              </DraggableWrapper>
            </Draggable>
          )}
        </Default>
      </Switch>

      <SessionTranscriptionModal
        isOpen={isTranscriptionModalOpen}
        onClose={closeTranscriptionSettings}
        isEnabled={isTranscriptionEnabled}
        setIsEnabled={setIsTranscriptionEnabled}
      />

      <SessionCustomisationModal
        isOpen={isCustomisationModalOpen}
        onClose={() => setIsCustomisationModalOpen(false)}
      />
    </ChatContainer>,
    document.body
  );
};

export default AiChat;
